// ============================================================
// [2.13.0] Console du serveur de comptes — /sso?audience=<origine>
// Consentement avant d'ouvrir la console d'un serveur Siverix avec son compte
// Siverix. Sur « Continuer » : assertion pour cette origine, puis retour vers
// <origine>/admin/#/sso-retour (adresse TOUJOURS dérivée de l'audience, jamais
// d'un paramètre libre). Demande seulement d'être connecté (pas admin).
// ============================================================

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { buildSsoReturnUrl, parseConsoleAudience } from '@siverix/shared';
import { api } from '../api';
import { useAuth } from '../auth/AuthContext';

export function SsoConsentPage(): JSX.Element {
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  const audience = parseConsoleAudience(params.get('audience'));
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!audience.ok) {
    return (
      <Card>
        <h1 style={titleStyle}>Demande de connexion invalide</h1>
        <p role="alert" style={{ color: 'var(--color-error)', fontSize: 14 }}>
          {audience.reason === 'missing'
            ? "Aucun serveur Siverix n'est indiqué dans ce lien."
            : "L'adresse du serveur Siverix doit être une adresse http(s) seule, sans chemin, sans paramètre ni fragment."}
        </p>
        <Link to="/">Aller à la console</Link>
      </Card>
    );
  }

  const origin = audience.origin;

  if (cancelled) {
    return (
      <Card>
        <h1 style={titleStyle}>C'est annulé</h1>
        <p style={{ fontSize: 14 }}>
          Rien n'a été envoyé à <strong style={{ wordBreak: 'break-all' }}>{origin}</strong>. Tu
          peux fermer cet onglet.
        </p>
        <Link to="/">Aller à la console</Link>
      </Card>
    );
  }

  async function proceed(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const { assertion, issuer } = await api.ssoAssertion(origin);
      // replace : l'écran de consentement ne reste pas dans l'historique.
      window.location.replace(buildSsoReturnUrl(origin, assertion, issuer));
    } catch (e: unknown) {
      setErr((e as Error).message || 'La connexion a échoué.');
      setBusy(false);
    }
  }

  const pseudo = user?.displayName ?? user?.username ?? '';

  return (
    <Card>
      <h1 style={titleStyle}>Console d'un serveur Siverix</h1>
      <div
        data-testid="sso-audience"
        style={{
          margin: '16px 0',
          padding: '14px 16px',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          fontSize: 22,
          fontWeight: 700,
          fontFamily: 'JetBrains Mono, Consolas, monospace',
          wordBreak: 'break-all',
          textAlign: 'center',
        }}
      >
        {origin}
      </div>
      <p style={{ fontSize: 15, margin: '0 0 8px' }}>
        Ouvrir la console de <strong style={{ wordBreak: 'break-all' }}>{origin}</strong> avec ton
        compte <strong>{pseudo}</strong>
        {user?.displayName ? (
          <span style={{ color: 'var(--color-text-muted)' }}> (@{user.username})</span>
        ) : null}{' '}
        ?
      </p>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
        Continue seulement si tu reconnais cette adresse. Ce serveur Siverix recevra ton pseudo, ton
        adresse email et ton avatar, et t'ouvrira une session sur sa console.
      </p>
      {origin.startsWith('http://') && (
        <p style={{ fontSize: 13, color: 'var(--color-error)', margin: '8px 0 0' }}>
          Attention : cette adresse n'est pas chiffrée (http).
        </p>
      )}

      {err && (
        <div
          role="alert"
          style={{
            marginTop: 14,
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(248,81,73,.15)',
            color: 'var(--color-error)',
            fontSize: 13,
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button
          type="button"
          onClick={() => setCancelled(true)}
          disabled={busy}
          style={secondaryButtonStyle}
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={() => void proceed()}
          disabled={busy}
          style={primaryButtonStyle}
        >
          {busy ? '…' : 'Continuer'}
        </button>
      </div>

      <p style={{ marginTop: 16, fontSize: 12, color: 'var(--color-text-muted)' }}>
        Ce n'est pas ton compte ?{' '}
        <button type="button" onClick={() => void logout()} style={linkButtonStyle}>
          Changer de compte
        </button>
      </p>
    </Card>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 16 }}>
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 480,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = { marginTop: 0, fontSize: 20 };

const primaryButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '12px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-accent)',
  cursor: 'pointer',
  fontSize: 12,
};
